import { PageTitle } from '../components/shared/PageTitle';
import { useState } from 'react';
import { useParams } from 'react-router';
import { Button, Badge, Input, Spinner, Text, Field, Checkbox } from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useT } from '../i18n';
import {
  EVIDENCE_STAGE_IDS, fetchEvidenceShares, createEvidenceShare, revokeEvidenceShare,
  type EvidenceStageId, type CreatedShare,
} from '../api/shipment-evidence.api';

/** Enlaces para compartir evidencia de un embarque: crear, listar y revocar (E-23). */
export default function ShipmentEvidencePage() {
  const t = useT().shipmentEvidence;
  const { shipmentId = '' } = useParams();
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [days, setDays] = useState('7');
  const [stages, setStages] = useState<EvidenceStageId[]>([]);
  const [created, setCreated] = useState<CreatedShare | null>(null);

  const list = useQuery({
    queryKey: ['evidence-shares', shipmentId],
    queryFn: () => fetchEvidenceShares(shipmentId),
    enabled: !!shipmentId,
  });

  const create = useMutation({
    mutationFn: () => createEvidenceShare(shipmentId, {
      label: label.trim() || undefined,
      days: Math.min(Math.max(parseInt(days, 10) || 7, 1), 30),
      stages: stages.length ? stages : undefined,
    }),
    onSuccess: (res) => {
      setCreated(res);
      setLabel('');
      void qc.invalidateQueries({ queryKey: ['evidence-shares', shipmentId] });
    },
  });

  const revoke = useMutation({
    mutationFn: (shareId: string) => revokeEvidenceShare(shipmentId, shareId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['evidence-shares', shipmentId] }),
  });

  const publicUrl = (token: string) => `${window.location.origin}/evidencia-compartida?token=${encodeURIComponent(token)}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16, maxWidth: 720 }}>
      <PageTitle>{t.title}</PageTitle>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Text as="h2" size={500} weight="semibold">{t.createTitle}</Text>
        <Field label={t.label}>
          <Input value={label} onChange={(_, d) => setLabel(d.value)} maxLength={100} />
        </Field>
        <Field label={t.days}>
          <Input type="number" min={1} max={30} value={days} onChange={(_, d) => setDays(d.value)} />
        </Field>
        <Text weight="semibold">{t.stages}</Text>
        {EVIDENCE_STAGE_IDS.map((id) => (
          <Checkbox
            key={id}
            label={t.stageNames[id]}
            checked={stages.includes(id)}
            onChange={(_, d) => setStages((prev) => (d.checked ? [...prev, id] : prev.filter((x) => x !== id)))}
          />
        ))}
        <Button appearance="primary" onClick={() => create.mutate()} disabled={create.isPending}>
          {t.create}
        </Button>
        {created && (
          <div role="status" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Text>{t.created}</Text>
            <Text font="monospace" style={{ wordBreak: 'break-all' }}>{publicUrl(created.token)}</Text>
            <Button onClick={() => void navigator.clipboard.writeText(publicUrl(created.token))}>{t.copy}</Button>
          </div>
        )}
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Text as="h2" size={500} weight="semibold">{t.shareTitle}</Text>
        {list.isLoading && <Spinner label={t.loading} />}
        {list.data && list.data.length === 0 && <Text>{t.none}</Text>}
        {list.data?.map((s) => (
          <div key={s.shareId} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Text weight="semibold">{s.label ?? s.shareId.slice(0, 8)}</Text>
            <Badge appearance="tint" color={s.active ? 'success' : 'subtle'}>
              {s.revokedAt ? t.revoked : s.active ? t.active : t.expired}
            </Badge>
            <Text size={200}>{t.expiresOn} {new Date(s.expiresAt).toLocaleDateString()}</Text>
            {s.active && (
              <Button size="small" onClick={() => revoke.mutate(s.shareId)} disabled={revoke.isPending}>{t.revoke}</Button>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}
